import { z } from 'zod';

const couponSchema = z
  .object({
    duration: z.string(),
    percent_off: z.number(),
  })
  .nullable()
  .optional();

const upfrontAmountSchema = z.object({
  original_amount: z.number(),
  payable_amount: z.number(),
  currency: z.string(),
});

const paymentPlanSchema = z.object({
  type: z.string(),
  recurring_interval: z.string().optional(),
  recurring_interval_count: z.number().optional(),
  upfront_amount: upfrontAmountSchema,
});

export const dynamicPriceSchema = z
  .object({
    results: z.array(
      z.object({
        coupon: couponSchema,
        payment_plans_v2: z.array(paymentPlanSchema),
        degree_application_amount: z.number(),
        // Braavos' own rendering of the fee, e.g. "$199 USD" / "$217 CAD".
        // Required and non-empty: every request this schema parses sends
        // `add_degree_application=true`, so the field is always populated. An
        // empty string has to fail here rather than reach the UI — consumers
        // read a falsy fee as "still loading", so it would render a skeleton
        // that never resolves instead of the priceless fallback copy.
        degree_application_amount_display: z.string().nonempty(),
        price: z.object({
          currency: z.string(),
        }),
      })
    ),
  })
  .transform((data) => {
    const result = data.results[0];
    const monthlyPlan = result.payment_plans_v2.find(
      (plan) => plan.type === 'recurring' && plan.recurring_interval_count === 1
    );

    if (!monthlyPlan) {
      throw new Error('No monthly recurring payment plan found');
    }

    return {
      monthlyOriginalAmount: monthlyPlan.upfront_amount.original_amount,
      monthlyPayableAmount: monthlyPlan.upfront_amount.payable_amount,
      enrollmentFee: result.degree_application_amount,
      currency: result.price.currency,
      isForeverCoupon: result.coupon?.duration === 'forever',
      percentOff: result.coupon?.percent_off ?? null,
      enrollmentFeeDisplay: result.degree_application_amount_display,
    };
  });

export type DynamicPrice = z.infer<typeof dynamicPriceSchema>;
